/**
 * What the triage desk does with the answer of POST /leads/:id/verify-manually.
 *
 * 2026-10-08: the row flips to verified, and the toast says "Verified", only
 * when the server says the lead IS verified (D14: every required + visible
 * field answered). A partial save keeps the row's status and names the gaps.
 * A server too old to send `verified` keeps the previous behaviour.
 */
import type { DashboardRow, Tab, VerifySchemaField, VerifyValues } from './triageDeskTypes';

export interface VerifyManualResult {
  verifiedByName?: string | null;
  score?: number | null;
  interestLevel?: string | null;
  businessSegment?: string | null;
  verified?: boolean;
  verificationStatus?: string | null;
  missingFields?: Array<{ key: string; label: string }>;
}

const isVerified = (data: VerifyManualResult): boolean => data.verified !== false;

export function rowAfterVerify(row: DashboardRow, payload: Record<string, unknown>, data: VerifyManualResult): DashboardRow {
  const derivedSegment = data.businessSegment
    ?? (payload.applicationType ? (payload.applicationType === 'B2B' ? 'B2B' : 'B2C') : null);
  return {
    ...row,
    ...(payload.name            ? { name: String(payload.name) } : {}),
    ...(payload.location        ? { location: String(payload.location) } : {}),
    ...(payload.locationState   ? { state: String(payload.locationState) } : {}),
    ...(payload.product         ? { requirement: String(payload.product) } : {}),
    ...(payload.applicationType ? { applicationType: String(payload.applicationType) } : {}),
    ...(derivedSegment          ? { businessSegment: derivedSegment } : {}),
    ...(payload.leadCapacity    ? { capacity: String(payload.leadCapacity) } : {}),
    ...(payload.leadTimeline    ? { timeline: String(payload.leadTimeline) } : {}),
    ...(payload.indicativePriceShared === true ? { priceShared: true } : {}),
    ...(payload.indicativePriceRange ? { priceRange: String(payload.indicativePriceRange) } : {}),
    ...(data.score !== undefined && data.score !== null ? { score: data.score } : {}),
    ...(data.interestLevel ? { tier: data.interestLevel as DashboardRow['tier'] } : {}),
    ...(isVerified(data)
      ? { verifiedStatus: 'verified' as const, verifiedBy: 'manual' as const, verifiedByName: data.verifiedByName ?? null,
          detailsComplete: true, verificationStatus: data.verificationStatus ?? row.verificationStatus ?? null }
      : { detailsComplete: false, ...(data.verificationStatus ? { verificationStatus: data.verificationStatus } : {}) }),
  };
}

export function verifyToastText(who: string, data: VerifyManualResult): string {
  if (isVerified(data)) return `Verified ${who}`;
  const missing = (data.missingFields ?? []).map((f) => f.label).filter(Boolean);
  return missing.length > 0
    ? `Answers saved for ${who} — still needed: ${missing.join(', ')}`
    : `Answers saved for ${who} — not verified yet`;
}

/**
 * 2026-10-09 — the action in the Verified column. Before, only an unverified
 * row had one, so a lead verified half-filled (a LeadVerification row says
 * 'verified', LeadImport.verificationStatus still needs_manual_verify) could
 * never be fixed: Deepika's 23 re-check leads.
 *  - 'verify'             unverified row nobody started
 *  - 'recheck_incomplete' started (manual / Avika / ChatKart) but required details missing
 *  - 'recheck'            verified and complete, an older server, or Overdue HOT
 *                         (that feed carries no verification snapshot)
 */
export type VerifyAction = 'verify' | 'recheck_incomplete' | 'recheck';

export function verifyActionFor(row: DashboardRow, tab: Tab): VerifyAction {
  if (tab === 'hot_overdue') return 'recheck';
  // BUG 1b: the server says 'verified' only for complete details; an unverified
  // row that carries a source (Avika chat, a half-filled manual save) was
  // started — re-check it. (detailsComplete === false on a verified row = a
  // server from before 1b.)
  if (row.verifiedStatus === 'verified') return row.detailsComplete === false ? 'recheck_incomplete' : 'recheck';
  return row.verifiedBy ? 'recheck_incomplete' : 'verify';
}

/**
 * Prefill the Verify dialog from GET /leads/:id/verify-values (stored columns +
 * the collected answers — the same bag the server's completeness check reads).
 * Only EMPTY keys are filled, so nothing already shown or typed is replaced.
 * Booleans become Yes/No (the dialog's boolean selects); a fixed-option select
 * never takes an out-of-options legacy value (same rule as openVerify).
 */
export function fillEmptyFromStored(
  shown: VerifyValues,
  stored: Record<string, unknown>,
  schema: VerifySchemaField[],
): VerifyValues {
  const out: VerifyValues = { ...shown };
  for (const f of schema) {
    if ((out[f.key] ?? '').trim()) continue;
    const raw = stored[f.key];
    let val: string;
    if (typeof raw === 'boolean') val = raw ? 'Yes' : 'No';
    else if (typeof raw === 'number' && Number.isFinite(raw)) val = String(raw);
    else if (typeof raw === 'string') val = raw.trim();
    else continue;
    if (!val) continue;
    if (f.type === 'select' && !f.source && f.options && f.options.length > 0 && !f.options.includes(val)) continue;
    out[f.key] = val;
  }
  for (const k of Object.keys(out)) if (!out[k]) delete out[k];
  return out;
}
